import { useCallback } from 'react';
import {
    QueryClient,
    skipToken,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { ApiHandler } from '../handlers';
import { Donee } from '../models';
import { logError } from '../../utils/logError';

const DONEE_QUERY_KEY = 'currentDonee';

const doneeQueryFn = async (
    id: string,
    clientAccessToken: string,
): Promise<Donee> => {
    const response = await ApiHandler.instance.home.getDonee(
        id,
        clientAccessToken,
    );
    if (!response.success) {
        if (response.error.status === 500 && !response.error.isAxiosError) {
            logError('API Request Error: 500', response.error);
        }
        throw new Error(response.error.message || 'Failed to fetch donee');
    }
    return { ...response.data.response.payload, idShort: id };
};

export const clearQueriesExceptDonee = (queryClient: QueryClient) => {
    queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== DONEE_QUERY_KEY,
    });
    queryClient.getMutationCache().clear();
};

const useDoneeQuery = (): UseQueryResult<Donee> => {
    return useQuery<Donee>({
        queryKey: [DONEE_QUERY_KEY],
        queryFn: skipToken,
        staleTime: 1000 * 60 * 5,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

export const useDonee = () => {
    const queryClient = useQueryClient();
    const { data, isFetched, isFetching, isLoading, error } = useDoneeQuery();

    const fetchDonee = useCallback(
        (id: string, clientAccessToken: string): Promise<Donee | undefined> =>
            queryClient
                .fetchQuery({
                    queryKey: [DONEE_QUERY_KEY],
                    queryFn: () => doneeQueryFn(id, clientAccessToken),
                    staleTime: 0,
                })
                .catch(() => undefined),
        [queryClient],
    );

    return {
        donee: data,
        isLoading,
        hasNeverFetched: !isFetched && !isFetching,
        error,
        fetchDonee,
    };
};
